import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
 
const AuthContext = createContext(null);
 
// Decode JWT token payload without a library
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    return payload;
  } catch (e) {
    return null;
  }
}
 
function isTokenExpired(token) {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  // Consider expired 60 seconds before actual expiry for safety margin
  return Date.now() >= (payload.exp * 1000) - 60000;
}
 
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
 
  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
  }, []);
 
  useEffect(() => {
    // Check if user is stored in localStorage
    const storedUser = localStorage.getItem('currentUser');
    const storedToken = localStorage.getItem('token');
 
    if (storedUser && storedToken) {
      // Validate token hasn't expired
      if (isTokenExpired(storedToken)) {
        clearAuth();
      } else {
        try {
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
        } catch (e) {
          clearAuth();
        }
      }
    }
    setLoading(false);
  }, [clearAuth]);
 
  // Periodically check token expiry (every minute)
  useEffect(() => {
    if (!token) return;
 
    const interval = setInterval(() => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && isTokenExpired(storedToken)) {
        clearAuth();
        window.location.href = '/login';
      }
    }, 60000);
 
    return () => clearInterval(interval);
  }, [token, clearAuth]);
 
  const login = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('currentUser', JSON.stringify(userData));
  }, []);
 
  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);
 
  const getToken = useCallback(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken && isTokenExpired(storedToken)) {
      clearAuth();
      return null;
    }
    return storedToken;
  }, [clearAuth]);
 
  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};
 
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
